import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API from "./config/API";

function CreateAddRoomForm() {
  const [floors, setFloors] = useState([]);
  const [floorStatusMap, setFloorStatusMap] = useState({});
  const [rooms, setRooms] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [popup, setPopup] = useState("");
  const [popupType, setPopupType] = useState("success");

  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const emailId = user?.emailId || "";

  // ================= POPUP =================
  const showPopup = (msg, type = "error") => {
    setPopup(msg);
    setPopupType(type);

    setTimeout(() => {
      setPopup("");
    }, 2500);
  };

  // ================= LOAD FLOORS =================
  useEffect(() => {
    loadFloors();
  }, []);

  // ================= LOAD FLOORS FUNCTION =================
  const loadFloors = () => {
    const currentUser = JSON.parse(localStorage.getItem("user"));

    if (!currentUser?.emailId || !currentUser?.jwtToken) {
      localStorage.clear();
      navigate("/");
      return;
    }

    axios
      .get(`${API.V1}/floor_rooms/getFloors`, {
        params: {
          emailId: currentUser.emailId,
        },
        headers: {
          Authorization: `Bearer ${currentUser.jwtToken}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        if (res.data.statusCode === 200) {
          setFloors(res.data.data.existedfloors || []);
          setFloorStatusMap(res.data.data.roomsCreatedAlreadyByThisFloor || {});
        } else {
          showPopup(res.data.message || "Failed to load floors", "error");
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.clear();

          showPopup("Session Expired. Please login again.", "error");

          setTimeout(() => {
            navigate("/");
          }, 1000);

          return;
        }

        showPopup("Failed to load floors", "error");
      });
  };

  // ================= FETCH ROOMS =================
  const fetchRooms = async (floorName) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user"));

      const res = await axios.get(
        `${API.V1}/maxLimit/getFloorsRoomsNamesWithMaxCountDetails`,
        {
          params: {
            floorName,
            emailId,
          },
          headers: {
            Authorization: `Bearer ${currentUser?.jwtToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      // ================= SUCCESS =================
      if (res.data.statusCode === 200) {
        const data = res.data.data.roomList || [];

        const formatted = data.map((r) => ({
          roomName: r.roomName,
          floorId: r.floorId,
          floorName: r.floorName,
          createdBy: emailId,
          isSnoringRoom: false,
          isLockerAvailable: false,
        }));

        setRooms(formatted);
        setErrorMsg("");
      }

      // ================= BACKEND VALIDATION =================
      else if (res.data.statusCode === 400) {
        setRooms([]);
        setErrorMsg(res.data.message);

        showPopup(res.data.message, "error");
      } else {
        setRooms([]);
        setErrorMsg(res.data.message || "Unable to load rooms");

        showPopup(res.data.message || "Unable to load rooms", "error");
      }
    } catch (error) {
      // ================= TOKEN EXPIRED =================
      if (error.response?.status === 401) {
        localStorage.clear();

        showPopup("Session Expired. Please login again.", "error");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      // ================= BACKEND VALIDATION =================
      if (error.response?.data) {
        const apiMessage =
          error.response.data.message || "Room creation exceeded";

        setRooms([]);
        setErrorMsg(apiMessage);

        showPopup(apiMessage, "error");
      } else {
        showPopup("Failed to load rooms", "error");
      }
    }
  };

  // ================= FLOOR CHANGE =================
  const handleFloorChange = (e) => {
    const floorId = e.target.value;

    setSelectedFloor(floorId);
    setRooms([]);
    setErrorMsg("");

    const floor = floors.find((f) => String(f.floorId) === String(floorId));

    if (!floor) {
      return;
    }

    fetchRooms(floor.floorName);
  };

  // ================= DELETE ROOM =================
  const deleteRoom = (index) => {
    setRooms((previousRooms) => previousRooms.filter((_, i) => i !== index));
  };

  // ================= SNORING CHANGE =================
  const handleSnoringChange = (index, value) => {
    setRooms((previousRooms) =>
      previousRooms.map((room, i) =>
        i === index
          ? {
              ...room,
              isSnoringRoom: value === "true",
            }
          : room,
      ),
    );
  };

  // ================= LOCKER CHANGE =================
  const handleLockerChange = (index, value) => {
    setRooms((previousRooms) =>
      previousRooms.map((room, i) =>
        i === index
          ? {
              ...room,
              isLockerAvailable: value === "true",
            }
          : room,
      ),
    );
  };

  // ================= SAVE =================
  const handleSubmit = async () => {
    if (errorMsg) {
      return showPopup(errorMsg, "error");
    }

    if (!selectedFloor) {
      return showPopup("Please select a floor", "error");
    }

    if (rooms.length === 0) {
      return showPopup("No rooms available", "error");
    }

    try {
      const currentUser = JSON.parse(localStorage.getItem("user"));

      const res = await axios.post(`${API.V1}/floor_rooms/createRoom`, rooms, {
        headers: {
          Authorization: `Bearer ${currentUser?.jwtToken}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data.statusCode === 200) {
        showPopup(res.data.message || "Rooms saved successfully", "success");

        setRooms([]);
        setSelectedFloor("");
        setErrorMsg("");

        loadFloors();
      } else {
        showPopup(res.data.message || "Failed to save rooms", "error");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();

        showPopup("Session Expired. Please login again.", "error");

        setTimeout(() => {
          navigate("/");
        }, 1000);

        return;
      }

      if (error.response?.data?.message) {
        showPopup(error.response.data.message, "error");
      } else {
        showPopup("Server Error", "error");
      }
    }
  };

  // ================= BACK =================
  const handleBack = () => {
    navigate("/floorRoomsBeds");
  };

  return (
    <div style={styles.container}>
      {/* ================= POPUP ================= */}
      {popup && (
        <div
          style={{
            ...styles.popup,
            background:
              popupType === "success"
                ? "linear-gradient(135deg, #16a34a, #22c55e)"
                : "linear-gradient(135deg, #dc2626, #ef4444)",
          }}
        >
          <span style={styles.popupIcon}>
            {popupType === "success" ? "✓" : "!"}
          </span>

          <span>{popup}</span>
        </div>
      )}

      {/* ================= MAIN CARD ================= */}
      <div style={styles.card}>
        {/* ================= BACK BUTTON ================= */}
        <button
          type="button"
          style={styles.backBtn}
          onClick={handleBack}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)";
          }}
        >
          <span style={styles.backIcon}>←</span>
          Floor Rooms & Beds
        </button>

        {/* ================= HEADER ================= */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>🏢</div>

          <div>
            <h2 style={styles.title}>Floor Room Manager</h2>

            <p style={styles.subtitle}>
              Create and configure rooms for your PG floors
            </p>
          </div>
        </div>

        {/* ================= DIVIDER ================= */}
        <div style={styles.divider}></div>

        {/* ================= FLOOR SECTION ================= */}
        <div style={styles.section}>
          <label style={styles.mainLabel}>Select Floor</label>

          <div style={styles.selectWrapper}>
            <span style={styles.selectIcon}>🏢</span>

            <select
              style={styles.select}
              value={selectedFloor}
              onChange={handleFloorChange}
            >
              <option value="">Select a floor</option>

              {floors.map((f) => {
                const status = floorStatusMap[f.floorName];

                const isExceeded = status === "Room Creation Exceed";

                return (
                  <option key={f.floorId} value={f.floorId}>
                    {f.floorName}
                    {status ? ` - ${status}` : ""}
                  </option>
                );
              })}
            </select>

            <span style={styles.arrow}>⌄</span>
          </div>
        </div>

        {/* ================= ERROR ================= */}
        {errorMsg && (
          <div style={styles.error}>
            <span style={styles.errorIcon}>!</span>

            <span>{errorMsg}</span>
          </div>
        )}

        {/* ================= ROOM HEADER ================= */}
        {rooms.length > 0 && (
          <div style={styles.roomHeader}>
            <div>
              <h3 style={styles.roomHeading}>Available Rooms</h3>

              <p style={styles.roomSubheading}>
                Configure room facilities before saving
              </p>
            </div>

            <div style={styles.roomCount}>
              {rooms.length} {rooms.length === 1 ? "Room" : "Rooms"}
            </div>
          </div>
        )}

        {/* ================= ROOM GRID ================= */}
        <div style={styles.grid}>
          {rooms.map((room, i) => (
            <div key={`${room.roomName}-${i}`} style={styles.roomBox}>
              {/* ROOM NUMBER */}
              <div style={styles.roomTop}>
                <div style={styles.roomIcon}>🚪</div>

                <button
                  type="button"
                  style={styles.deleteBtn}
                  onClick={() => deleteRoom(i)}
                  title="Remove room"
                >
                  ×
                </button>
              </div>

              <h4 style={styles.roomTitle}>{room.roomName}</h4>

              {/* SNORING */}
              <div style={styles.field}>
                <label style={styles.label}>Is Snoring Room?</label>

                <select
                  style={styles.roomSelect}
                  value={room.isSnoringRoom.toString()}
                  onChange={(e) => handleSnoringChange(i, e.target.value)}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>

              {/* LOCKER */}
              <div style={styles.field}>
                <label style={styles.label}>Is Locker Available?</label>

                <select
                  style={styles.roomSelect}
                  value={room.isLockerAvailable.toString()}
                  onChange={(e) => handleLockerChange(i, e.target.value)}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </div>
            </div>
          ))}
        </div>

        {/* ================= EMPTY STATE ================= */}
        {!selectedFloor && rooms.length === 0 && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🏢</div>

            <h3 style={styles.emptyTitle}>Select a Floor</h3>

            <p style={styles.emptyText}>
              Choose a floor above to load the available rooms.
            </p>
          </div>
        )}

        {/* ================= SAVE BUTTON ================= */}
        <button
          type="button"
          style={{
            ...styles.saveBtn,
            opacity: !selectedFloor || rooms.length === 0 ? 0.6 : 1,
            cursor:
              !selectedFloor || rooms.length === 0 ? "not-allowed" : "pointer",
          }}
          onClick={handleSubmit}
          disabled={!selectedFloor || rooms.length === 0}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled) {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 8px 20px rgba(22,163,74,0.3)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(22,163,74,0.2)";
          }}
        >
          <span>💾</span>
          Save Rooms
        </button>
      </div>
    </div>
  );
}

export default CreateAddRoomForm;

/* =========================================================
   MODERN STYLES
========================================================= */

const styles = {
  container: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background:
      "linear-gradient(135deg, #e0f2fe 0%, #f8fafc 45%, #eef2ff 100%)",
    fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
    padding: "30px 20px",
    boxSizing: "border-box",
  },

  card: {
    width: "680px",
    maxWidth: "100%",
    minHeight: "500px",
    background: "#ffffff",
    borderRadius: "22px",
    padding: "30px",
    boxSizing: "border-box",
    position: "relative",
    boxShadow: "0 20px 60px rgba(15,23,42,0.12)",
    border: "1px solid rgba(148,163,184,0.18)",
  },

  /* ================= BACK BUTTON ================= */

  backBtn: {
    position: "absolute",
    top: "22px",
    left: "22px",
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "9px 14px",
    background: "#ffffff",
    color: "#1e40af",
    border: "1px solid #dbeafe",
    borderRadius: "10px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "700",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    transition: "all 0.2s ease",
    zIndex: 5,
  },

  backIcon: {
    fontSize: "18px",
    lineHeight: "1",
  },

  /* ================= HEADER ================= */

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "14px",
    marginTop: "35px",
  },

  headerIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #dbeafe, #e0e7ff)",
    fontSize: "24px",
  },

  title: {
    margin: "0",
    fontSize: "23px",
    fontWeight: "750",
    color: "#0f172a",
  },

  subtitle: {
    margin: "5px 0 0",
    fontSize: "13px",
    color: "#64748b",
  },

  divider: {
    height: "1px",
    background: "#e2e8f0",
    margin: "25px 0",
  },

  /* ================= FLOOR ================= */

  section: {
    marginBottom: "15px",
  },

  mainLabel: {
    display: "block",
    marginBottom: "8px",
    color: "#334155",
    fontSize: "14px",
    fontWeight: "700",
  },

  selectWrapper: {
    position: "relative",
    width: "100%",
  },

  selectIcon: {
    position: "absolute",
    left: "13px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "17px",
    zIndex: 2,
    pointerEvents: "none",
  },

  select: {
    width: "100%",
    height: "48px",
    padding: "0 40px 0 42px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    outline: "none",
    background: "#f8fafc",
    color: "#0f172a",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxSizing: "border-box",
    appearance: "none",
  },

  arrow: {
    position: "absolute",
    right: "15px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#64748b",
    fontSize: "20px",
    pointerEvents: "none",
  },

  /* ================= ERROR ================= */

  error: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    padding: "12px 14px",
    marginTop: "12px",
    marginBottom: "15px",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
  },

  errorIcon: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#dc2626",
    color: "#ffffff",
    fontWeight: "bold",
    flexShrink: 0,
  },

  /* ================= ROOM HEADER ================= */

  roomHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "24px",
    marginBottom: "14px",
  },

  roomHeading: {
    margin: "0",
    color: "#0f172a",
    fontSize: "17px",
    fontWeight: "750",
  },

  roomSubheading: {
    margin: "4px 0 0",
    color: "#64748b",
    fontSize: "12px",
  },

  roomCount: {
    padding: "7px 11px",
    borderRadius: "20px",
    background: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "12px",
    fontWeight: "700",
    border: "1px solid #dbeafe",
  },

  /* ================= ROOM GRID ================= */

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "15px",
    marginTop: "15px",
  },

  roomBox: {
    position: "relative",
    background: "linear-gradient(145deg, #eff6ff, #f8fafc)",
    padding: "17px",
    borderRadius: "15px",
    border: "1px solid #dbeafe",
    boxShadow: "0 5px 15px rgba(30,64,175,0.06)",
    boxSizing: "border-box",
  },

  roomTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "7px",
  },

  roomIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#dbeafe",
    fontSize: "17px",
  },

  deleteBtn: {
    width: "27px",
    height: "27px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: "50%",
    background: "#fee2e2",
    color: "#dc2626",
    cursor: "pointer",
    fontSize: "17px",
    fontWeight: "bold",
  },

  roomTitle: {
    margin: "0 0 16px",
    color: "#1e3a8a",
    fontWeight: "750",
    fontSize: "17px",
  },

  field: {
    marginBottom: "13px",
  },

  label: {
    display: "block",
    marginBottom: "6px",
    color: "#475569",
    fontSize: "12px",
    fontWeight: "650",
  },

  roomSelect: {
    width: "100%",
    height: "38px",
    padding: "0 10px",
    borderRadius: "9px",
    border: "1px solid #cbd5e1",
    outline: "none",
    background: "#ffffff",
    color: "#1e293b",
    fontSize: "13px",
    cursor: "pointer",
    boxSizing: "border-box",
  },

  /* ================= EMPTY STATE ================= */

  emptyState: {
    marginTop: "25px",
    padding: "35px 20px",
    textAlign: "center",
    borderRadius: "15px",
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
  },

  emptyIcon: {
    fontSize: "38px",
    marginBottom: "8px",
  },

  emptyTitle: {
    margin: "0",
    color: "#334155",
    fontSize: "16px",
  },

  emptyText: {
    margin: "7px 0 0",
    color: "#64748b",
    fontSize: "13px",
  },

  /* ================= SAVE ================= */

  saveBtn: {
    width: "100%",
    height: "48px",
    marginTop: "25px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    border: "none",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #16a34a, #22c55e)",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "750",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(22,163,74,0.2)",
    transition: "all 0.2s ease",
  },

  /* ================= POPUP ================= */

  popup: {
    position: "fixed",
    top: "22px",
    right: "22px",
    minWidth: "260px",
    maxWidth: "420px",
    padding: "13px 17px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "#ffffff",
    borderRadius: "11px",
    fontSize: "13px",
    fontWeight: "650",
    boxShadow: "0 12px 30px rgba(0,0,0,0.2)",
    zIndex: 9999,
  },

  popupIcon: {
    width: "22px",
    height: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    color: "#ffffff",
    fontWeight: "bold",
    flexShrink: 0,
  },
};
